import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { CloneCard } from "src/components/CloneCard";
import { SortingDropdownList } from "src/components/SortingDropdownList";
import { Pagination } from "src/components/Pagination";
import { ISelectOption } from "src/@types/form";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { getMyClonesAsync, ITEMS_PER_PAGE } from "src/redux/myClones/actions";
import { selectorMyClones } from "src/redux/myClones/selector";
import { Loader } from "src/components/Loader";
import { Sizes } from "src/@types/sizes";
import { useQueryParams } from "src/hooks/useQueryParams";
import { MY_CLONE_SORT_OPTIONS } from "src/page-components/cabinet/my-clones/constants";
import { Button } from "src/components/Button";
import { ButtonVariants } from "src/components/Button/types";
import { ReactComponent as PlusIcon } from "src/assets/icons/plus.svg";
import { Link } from "src/components/Link";
import { PATHNAMES } from "src/constants/routes";
import { myClonesTrns } from "src/page-components/cabinet/my-clones/trns";
import { EmptyCloneList } from "src/page-components/cabinet/my-clones/EmptyCloneList";
import { MetadataWrapper } from "src/components/MetadataWrapper";

const QUERY_KEY_STATUS = "status";
const QUERY_KEY_PAGE = "page";

const MyClones = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { queryParams, getQueryParam, setQueryParam } = useQueryParams();

  const { data, isLoading } = useAppSelector(selectorMyClones);

  const activeOption = MY_CLONE_SORT_OPTIONS.find(
    ({ value }) => value === getQueryParam(QUERY_KEY_STATUS)
  );

  const setOption = ({ value }: ISelectOption) => {
    setQueryParam(QUERY_KEY_PAGE, "1");
    setQueryParam(QUERY_KEY_STATUS, value);
  };

  useEffect(() => {
    if (!activeOption) setOption(MY_CLONE_SORT_OPTIONS[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = queryParams.toString();
    dispatch(getMyClonesAsync(params));
  }, [dispatch, queryParams]);

  const myClonesResult = data?.results || [];

  const page = Number(getQueryParam(QUERY_KEY_PAGE)) || 1;
  const pageCount = Math.ceil(data?.count / ITEMS_PER_PAGE);
  const setPage = (page: number) => setQueryParam(QUERY_KEY_PAGE, page);

  const isPaginated = pageCount > 1;

  return (
    <MetadataWrapper name="my_clones">
      {isLoading ? (
        <div className="flex justify-center items-center min-h-72">
          <Loader size={Sizes.L} />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <SortingDropdownList
              options={MY_CLONE_SORT_OPTIONS}
              activeOption={activeOption}
              setOption={setOption}
              isStatus
            />
            <Button variant={ButtonVariants.SECONDARY} className="!p-0">
              <Link
                href={PATHNAMES.CREATE_CLONE}
                className="flex items-center justify-center gap-2 py-2 px-5 font-medium"
              >
                <PlusIcon />
                <span>{t(myClonesTrns.createCloneButton)}</span>
              </Link>
            </Button>
          </div>

          <div className="flex flex-col gap-5">
            {myClonesResult?.length > 0 ? (
              Boolean(myClonesResult?.length) && (
                <div
                  className={cn("grid grid-cols-1 gap-3", {
                    "sm:grid-cols-2 md:grid-cols-1": myClonesResult.length > 1,
                  })}
                >
                  {myClonesResult.map((clone) => (
                    <CloneCard key={clone.id} clone={clone} isEditable />
                  ))}
                </div>
              )
            ) : (
              <EmptyCloneList />
            )}

            {isPaginated && (
              <Pagination
                className="self-center my-8"
                page={page}
                pageCount={pageCount}
                setPage={setPage}
              />
            )}
          </div>
        </div>
      )}
    </MetadataWrapper>
  );
};

export default MyClones;
